import { FormEvent, useState } from 'react';
import { AuthFailure, Badge, currentMonth, dateTime, Empty, money, number, Pagination, ResourceState, SectionHeading, Stat, useResource } from './shared';
import { SettlementResponse } from './types';
import { MonthlySettlementsView } from './MonthlySettlementsView';

const labels = {none: '정산 없음', pending: '정산 대기', paid: '지급 완료', hold: '보류'};

export function SettlementsView({onAuth}: {onAuth: AuthFailure}) {
  const [monthly, setMonthly] = useState(true);
  return <><div className="pa-segmented" aria-label="정산 조회 방식"><button type="button" aria-pressed={monthly} className={monthly ? 'is-active' : ''} onClick={() => setMonthly(true)}>월별 정산</button><button type="button" aria-pressed={!monthly} className={!monthly ? 'is-active' : ''} onClick={() => setMonthly(false)}>기존 기간별 조회</button></div>{monthly ? <MonthlySettlementsView onAuth={onAuth} /> : <LegacySettlementsView onAuth={onAuth} />}</>;
}

function LegacySettlementsView({onAuth}: {onAuth: AuthFailure}) {
  const [filters, setFilters] = useState({...currentMonth(), search: ''});
  const [draft, setDraft] = useState(filters);
  const [page, setPage] = useState(1);
  const [validation, setValidation] = useState('');
  const query = new URLSearchParams({...filters, page: String(page), limit: '20'});
  const state = useResource<SettlementResponse>(`/settlements?${query}`, onAuth);
  const search = (event: FormEvent) => {
    event.preventDefault();
    if (!draft.from || !draft.to || draft.from > draft.to) {setValidation('정산 기간을 올바르게 선택해 주세요.'); return;}
    setValidation(''); setPage(1); setFilters({...draft, search: draft.search.trim()});
  };
  return <>
    <form className="pa-filters pa-card" onSubmit={search}>
      <label>시작일<input type="date" value={draft.from} onChange={event => setDraft({...draft, from: event.target.value})} required /></label>
      <label>종료일<input type="date" value={draft.to} onChange={event => setDraft({...draft, to: event.target.value})} required /></label>
      <label className="pa-filter-grow">업체 검색<input placeholder="스토어명 또는 대표자명" value={draft.search} onChange={event => setDraft({...draft, search: event.target.value})} /></label>
      <button className="pa-button pa-button-primary" type="submit">조회하기</button>
    </form>
    {validation && <p className="pa-inline-error" role="alert">{validation}</p>}
    <ResourceState {...state} retry={state.reload}>{state.data && <>
      <div className="pa-stats">
        <Stat label="결제액" value={money(state.data.summary.gross_amount)} note={`${state.data.from} ~ ${state.data.to}`} />
        <Stat label="누적 환불액" value={money(state.data.summary.refund_amount)} />
        <Stat label="정산 기준액" value={money(state.data.summary.net_amount)} accent />
        <Stat label="지급 예정액" value={money(state.data.summary.payout_amount)} note={`${number(state.data.summary.payment_count)}건 결제`} />
      </div>
      <section className="pa-card pa-table-card">
        <SectionHeading title="업체별 정산 내역" description="실제 지급 처리는 은행 또는 정산 API 연동 전까지 운영자가 상태로 관리합니다." />
        {state.data.items.length === 0 ? <Empty>조회 조건에 맞는 업체가 없습니다.</Empty> : <div className="pa-table-wrap"><table>
          <thead><tr><th>업체</th><th>입금 계좌</th><th>상태</th><th className="pa-number">결제액</th><th className="pa-number">환불액</th><th className="pa-number">정산 기준액</th><th className="pa-number">지급 예정액</th><th>처리 정보</th></tr></thead>
          <tbody>{state.data.items.map(item => <tr key={item.store_seq}>
            <td><strong>{item.store_name || '이름 미등록'}</strong><small>스토어 #{item.store_seq} · {item.representative_name || '대표자 미등록'}</small></td>
            <td>{item.bank_name || '—'}<small>{item.bank_num || '계좌 미등록'}{item.depositor ? ` · ${item.depositor}` : ''}</small></td>
            <td><Badge value={item.settlement_status === 'paid' ? 'active' : item.settlement_status === 'hold' ? 'danger' : 'warning'}>{labels[item.settlement_status] || item.settlement_status}</Badge></td>
            <td className="pa-number">{money(item.gross_amount)}</td>
            <td className="pa-number">{money(item.refund_amount)}</td>
            <td className="pa-number pa-emphasis">{money(item.net_amount)}</td>
            <td className="pa-number">{money(item.payout_amount)}</td>
            <td>{item.paid_at ? `지급일 ${dateTime(item.paid_at, true)}` : dateTime(item.updated_at)}<small>{item.memo || item.updated_by || '처리 기록 없음'}</small></td>
          </tr>)}</tbody>
        </table></div>}
        <Pagination page={page} total={state.data.total} limit={state.data.limit} setPage={setPage} />
      </section>
    </>}</ResourceState>
  </>;
}
